import { Button, MetricRow, type MetricRowItem } from '@olytic/foundation';
import { TermTimeline } from '@olytic/foundation/cpq';

import { formatCurrency, formatDate, formatPercent } from '../objects/format';
import type { OpportunitySummary as Summary } from './opportunityPageApi';
import { scheduleStreams } from './pageModel';

/**
 * The cards between the header and the tabs: Amount (weighted beneath it),
 * Probability and Close Date (each double-click to edit) and the Schedule,
 * whose Edit opens the schedule editor.
 */
export function OpportunitySummary({
  summary,
  onSaveCloseDate,
  onSaveProbability,
  onEditSchedule,
}: {
  summary: Summary;
  onSaveCloseDate?: ((value: string | null) => Promise<void>) | undefined;
  onSaveProbability?: ((value: string | null) => Promise<void>) | undefined;
  onEditSchedule: () => void;
}): JSX.Element {
  const { opportunity } = summary;
  const probability = opportunity.probability === null ? null : formatPercent(opportunity.probability);
  const metrics: MetricRowItem[] = [
    {
      key: 'amount',
      label: 'Amount',
      value: formatCurrency(summary.contract_value),
      sub: probability ? `${formatCurrency(summary.weighted_value)} weighted` : 'No probability to weight it',
    },
    {
      key: 'probability',
      label: 'Probability',
      value: probability ?? '—',
      ...(onSaveProbability
        ? { edit: { kind: 'number' as const, value: opportunity.probability === null ? '' : String(opportunity.probability), onSave: onSaveProbability } }
        : {}),
    },
    {
      key: 'close-date',
      label: 'Close Date',
      value: opportunity.close_date ? formatDate(opportunity.close_date) : '—',
      ...(onSaveCloseDate ? { edit: { kind: 'date' as const, value: opportunity.close_date ?? '', onSave: onSaveCloseDate } } : {}),
    },
    {
      key: 'schedule',
      label: 'Schedule',
      span: 2,
      content: (
        <TermTimeline
          framed={false}
          streams={scheduleStreams(summary.schedule_lines)}
          action={
            summary.quote ? (
              <Button variant="link" size="sm" onClick={onEditSchedule}>
                Edit
              </Button>
            ) : undefined
          }
        />
      ),
    },
  ];
  return <MetricRow metrics={metrics} layout="wrap" surface="inverse" aria-label="Deal summary" />;
}
